import React, {useState} from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	TextField,
	Typography,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {pxToRem, responsiveFontSizes} from '../../utils/getFontValue';
import {DARK_MODE, forbiddenChars} from '../../constants';

const classNames = {
	dialogCointainer: {
		background: ({palette}) => (palette.mode === DARK_MODE ? 'black' : 'white'),
	},
	dialog: {
		padding: '1.5rem',
		width: '720px',
	},
	content: {
		padding: 4,
		backgroundColor: 'background.default',
	},
	heading: {
		color: '#FFA222',
		fontSize: pxToRem(12),
		...responsiveFontSizes({sm: 12, md: 14, lg: 18}),
	},
	text: {
		letterSpacing: 0.5,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		fontWeight: 'bold',
		fontSize: pxToRem(15),
		...responsiveFontSizes({sm: 15, md: 17, lg: 21}),
	},
	item: {
		paddingTop: '0.3rem',
		paddingBottom: '0.3rem',
	},
	titleLine: {
		flex: 1,
		height: '10px',
		borderRadius: '5px',
		backgroundColor: '#FFA222',
		marginLeft: 2,
	},
};

const CssTextField = styled(TextField)(({theme}) => ({
	'& .MuiOutlinedInput-root': {
		backgroundColor:
			theme.palette.mode === DARK_MODE
				? '#4b4b4b'
				: theme.palette.background.paper,
		'& fieldset': {
			border: 'none',
		},
		'&:hover fieldset': {
			border: 'none',
		},
		'&.Mui-focused fieldset': {
			border: 'none',
		},
		'& :-webkit-autofill': {
			'-webkit-box-shadow': theme.palette.mode === DARK_MODE 
				? '0 0 0 100px #4b4b4b inset'
				: '0 0 0 100px #fff inset',
			'-webkit-text-fill-color': theme.palette.mode === DARK_MODE ? '#fff' : '#000',
		},
	},
	'& .MuiInputBase-input': {
		color: theme.palette.mode === DARK_MODE ? '#FFFFFF' : 'inherit',
	},
	'& .MuiInputBase-input::placeholder': {
		color: theme.palette.mode === DARK_MODE ? '#B0B0B0' : 'inherit',
		opacity: 1,
	},
}));

const CssButton = styled(Button)({
	padding: '.5rem 1rem',
	marginLeft: '1rem !important',
	fontSize: pxToRem(18),
	...responsiveFontSizes({sm: 18, md: 20, lg: 28}),
	'&.Mui-disabled': {
		color: 'rgba(0, 0, 0, 0.26)',
		backgroundColor: '#d8d8d8',
	},
});

const EnterSerialNumber = ({
	open,
	familyName,
	partNumber,
	handleSubmit,
	handleClose,
}) => {
	const [serialNumber, setSerialNumber] = useState('');

	return (
		<Dialog
			open={open}
			sx={classNames.dialogCointainer}
			PaperProps={{sx: classNames.dialog}}
		>
			<form>
				<DialogTitle sx={{display: 'flex', alignItems: 'center'}}>
					<Typography sx={{ color: 'inherit' }}>MANUAL ENTRY</Typography>
					<Box sx={classNames.titleLine} />
				</DialogTitle>
				<DialogContent>
					<Box sx={classNames.content}>
						<Grid container direction='column' rowSpacing={3}>
							<Grid item container>
								<Grid item>
									<Typography sx={{ ...classNames.heading, ...classNames.item, color: 'inherit' }}>Part Family</Typography>
									<Typography sx={{ ...classNames.text, ...classNames.item, color: 'inherit' }}>{familyName}</Typography>
								</Grid>
								<Grid item paddingLeft={8}>
									<Typography sx={{ ...classNames.heading, ...classNames.item, color: 'inherit' }}>Part Number</Typography>
									<Typography sx={{ ...classNames.text, ...classNames.item, color: 'inherit' }}>{partNumber}</Typography>
								</Grid>
							</Grid>
							<Grid item sx={{width: '70%'}}>
								<Typography sx={{ ...classNames.heading, ...classNames.item, color: 'inherit' }}>
									Enter the barcode pattern here
								</Typography>
								<CssTextField
									id='enter-serial-number'
									autoFocus
									hiddenLabel
									fullWidth
									placeholder='Type here'
									size='small'
									variant='outlined'
									type='text'
									value={serialNumber}
									inputProps={{
										style: {
											paddingTop: '1rem',
											paddingBottom: '1rem',
										},
									}}
									data-testid='enter-serial-number-input-field'
									onChange={e => {
										const sanitizedValue = e.target.value.replace(forbiddenChars, '').toUpperCase();
										setSerialNumber(sanitizedValue)}
									}
								/>
							</Grid>
						</Grid>
					</Box>
				</DialogContent>
				<DialogActions>
					<CssButton size='small' onClick={() => handleClose()}>
						CANCEL
					</CssButton>
					<CssButton
						type='submit'
						size='small'
						variant='contained'
						disabled={!serialNumber}
						onClick={e => {
							e.preventDefault();
							handleSubmit(serialNumber);
						}}
					>
						CONFIRM
					</CssButton>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default EnterSerialNumber;
